import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Button,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Web as WebIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { PROVIDER_INFO_QUERY_KEY, useProviderInfo } from '../hooks/useProviderInfo';

const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [validationMessage, setValidationMessage] = React.useState(
    'Bonjour, votre description correspond. Vous pouvez venir récupérer votre objet.'
  );
  const [rejectionMessage, setRejectionMessage] = React.useState(
    "Bonjour, les informations ne correspondent pas à l'objet trouvé."
  );
  const [handoverMessage, setHandoverMessage] = React.useState(
    'Objet restitué en main propre. Merci !'
  );
  const [undoHandoverMessage, setUndoHandoverMessage] = React.useState(
    'La restitution en main propre a été annulée.'
  );
  const [deliveryVoucherMessage, setDeliveryVoucherMessage] = React.useState(
    "Bon de livraison : d’accord pour un bon Colissimo/UPS/Mondial Relay. Le paiement se fera sur Stripe par le perdeur, et une fois payé je pourrai imprimer le bon."
  );
  const [saveSuccess, setSaveSuccess] = React.useState<string | null>(null);

  const { data: providerInfo, isLoading, error } = useProviderInfo();

  React.useEffect(() => {
    const settings = providerInfo?.settings as Record<string, any> | undefined;
    const chat = settings?.chat_messages || {};
    setValidationMessage(
      typeof chat.validation_message === 'string' && chat.validation_message.trim() !== ''
        ? chat.validation_message
        : 'Bonjour, votre description correspond. Vous pouvez venir récupérer votre objet.'
    );
    setRejectionMessage(
      typeof chat.rejection_message === 'string' && chat.rejection_message.trim() !== ''
        ? chat.rejection_message
        : "Bonjour, les informations ne correspondent pas à l'objet trouvé."
    );
    setHandoverMessage(
      typeof chat.handover_message === 'string' && chat.handover_message.trim() !== ''
        ? chat.handover_message
        : 'Objet restitué en main propre. Merci !'
    );
    setUndoHandoverMessage(
      typeof chat.undo_handover_message === 'string' && chat.undo_handover_message.trim() !== ''
        ? chat.undo_handover_message
        : 'La restitution en main propre a été annulée.'
    );
    setDeliveryVoucherMessage(
      typeof chat.delivery_voucher_message === 'string' && chat.delivery_voucher_message.trim() !== ''
        ? chat.delivery_voucher_message
        : "Bon de livraison : d’accord pour un bon Colissimo/UPS/Mondial Relay. Le paiement se fera sur Stripe par le perdeur, et une fois payé je pourrai imprimer le bon."
    );
  }, [providerInfo]);

  const saveChatMessagesMutation = useMutation({
    mutationFn: async () => {
      if (!providerInfo?.id) throw new Error('Partenaire introuvable');
      const currentSettings = (providerInfo.settings as Record<string, any> | null) || {};
      const nextSettings = {
        ...currentSettings,
        chat_messages: {
          ...(currentSettings.chat_messages || {}),
          validation_message: validationMessage.trim(),
          rejection_message: rejectionMessage.trim(),
          handover_message: handoverMessage.trim(),
          undo_handover_message: undoHandoverMessage.trim(),
          delivery_voucher_message: deliveryVoucherMessage.trim(),
        },
      };
      return apiService.updateProvider(providerInfo.id, { settings: nextSettings });
    },
    onSuccess: () => {
      setSaveSuccess('Messages chat enregistrés.');
      queryClient.invalidateQueries({ queryKey: PROVIDER_INFO_QUERY_KEY });
      setTimeout(() => setSaveSuccess(null), 2500);
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Erreur lors du chargement des informations du partenaire
      </Alert>
    );
  }

  if (!providerInfo) {
    return (
      <Alert severity="warning">
        Aucune information de partenaire trouvée
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.35rem', sm: '2rem' } }}>
        Paramètres
      </Typography>

      <Grid container spacing={3}>
        {/* Informations générales */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations générales
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Nom de l'établissement"
                    secondary={providerInfo.name}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CategoryIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Type d'établissement"
                    secondary={
                      <Chip
                        label={providerInfo.type}
                        color="primary"
                        size="small"
                      />
                    }
                  />
                </ListItem>
                {providerInfo.description && (
                  <ListItem>
                    <ListItemText
                      primary="Description"
                      secondary={providerInfo.description}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact
              </Typography>
              <List>
                {providerInfo.email && (
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={providerInfo.email}
                    />
                  </ListItem>
                )}
                {providerInfo.phone && (
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Téléphone"
                      secondary={providerInfo.phone}
                    />
                  </ListItem>
                )}
                {providerInfo.website && (
                  <ListItem>
                    <ListItemIcon>
                      <WebIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Site web"
                      secondary={providerInfo.website}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Adresse */}
        {providerInfo.address && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Adresse
                </Typography>
                <List>
                  {providerInfo.address.street && (
                    <ListItem>
                      <ListItemIcon>
                        <LocationIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Rue"
                        secondary={providerInfo.address.street}
                      />
                    </ListItem>
                  )}
                  {providerInfo.address.city && (
                    <ListItem>
                      <ListItemText
                        primary="Ville"
                        secondary={providerInfo.address.city}
                      />
                    </ListItem>
                  )}
                  {providerInfo.address.postalCode && (
                    <ListItem>
                      <ListItemText
                        primary="Code postal"
                        secondary={providerInfo.address.postalCode}
                      />
                    </ListItem>
                  )}
                  {providerInfo.address.country && (
                    <ListItem>
                      <ListItemText
                        primary="Pays"
                        secondary={providerInfo.address.country}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Configuration du type de partenaire */}
        {providerInfo.providerType && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Configuration du type
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Nom des éléments"
                      secondary={providerInfo.providerType.itemName}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Attributs par défaut"
                      secondary={
                        <Box>
                          {Object.keys(providerInfo.providerType.defaultAttributes || {}).length > 0 ? (
                            Object.entries(providerInfo.providerType.defaultAttributes).map(([key, config]: [string, any]) => (
                              <Chip
                                key={key}
                                label={`${config.label || key}: ${config.type || 'text'}`}
                                size="small"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Aucun attribut configuré
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Messages chat
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Personnalisez les messages envoyés via les boutons « Je valide » et « Je ne valide pas » dans la messagerie.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Message de validation"
                    value={validationMessage}
                    onChange={(e) => setValidationMessage(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Message de non validation"
                    value={rejectionMessage}
                    onChange={(e) => setRejectionMessage(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label='Message "Restitué en main propre"'
                    value={handoverMessage}
                    onChange={(e) => setHandoverMessage(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label='Message "Annuler la restitution"'
                    value={undoHandoverMessage}
                    onChange={(e) => setUndoHandoverMessage(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label='Message "Bon de livraison"'
                    value={deliveryVoucherMessage}
                    onChange={(e) => setDeliveryVoucherMessage(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" gap={1} alignItems="center">
                    {saveSuccess && <Chip label={saveSuccess} color="success" size="small" />}
                    <Button
                      variant="contained"
                      onClick={() => saveChatMessagesMutation.mutate()}
                      disabled={saveChatMessagesMutation.isPending}
                    >
                      Enregistrer
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPage;